/**
 * Pieces the three tabs share.
 *
 * The state badges live here because their colours carry meaning: Overdue is
 * the one somebody has to act on, and it has to look different from Due at a
 * glance across a table of twenty riders.
 */

import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  BIKE_STATUS_LABELS,
  HIRE_PURCHASE_STATUS_LABELS,
  REMITTANCE_STATE_LABELS,
  type BikeStatus,
  type HirePurchaseStatus,
  type RemittanceState,
} from '@cenox/shared';
import { Badge, cn } from '../../components/ui';
import { money, moneyCompact } from '../../lib/format';
import { useAuth } from '../../lib/auth';

const REMITTANCE_TONES: Record<RemittanceState, 'neutral' | 'success' | 'warning' | 'danger' | 'info'> = {
  due: 'info',
  partially_paid: 'warning',
  paid: 'success',
  overdue: 'danger',
  waived: 'neutral',
};

export function RemittanceBadge({ state }: { state: RemittanceState }): ReactNode {
  return <Badge tone={REMITTANCE_TONES[state]}>{REMITTANCE_STATE_LABELS[state]}</Badge>;
}

const BIKE_TONES: Record<BikeStatus, 'neutral' | 'success' | 'warning' | 'danger' | 'info'> = {
  active: 'success',
  spare: 'neutral',
  under_repair: 'warning',
  hire_purchase: 'info',
  repossessed: 'danger',
  sold: 'neutral',
};

export function BikeStatusBadge({ status }: { status: BikeStatus }): ReactNode {
  return <Badge tone={BIKE_TONES[status]}>{BIKE_STATUS_LABELS[status]}</Badge>;
}

const AGREEMENT_TONES: Record<HirePurchaseStatus, 'neutral' | 'success' | 'warning' | 'danger' | 'info'> = {
  active: 'info',
  completed: 'success',
  suspended: 'warning',
  repossessed: 'danger',
};

export function AgreementBadge({ status }: { status: HirePurchaseStatus }): ReactNode {
  return <Badge tone={AGREEMENT_TONES[status]}>{HIRE_PURCHASE_STATUS_LABELS[status]}</Badge>;
}

/**
 * One figure, with the exact amount on hover.
 *
 * `hint` carries what the number MEANS where that is not obvious —
 * "outstanding" on the hire purchase tab is a receivable, not revenue, and
 * saying so on the tile is cheaper than a reader assuming otherwise.
 */
export function Stat({
  label,
  value,
  currency,
  count,
  hint,
  tone = 'plain',
}: {
  label: string;
  value?: string | null;
  currency?: string;
  count?: number;
  hint?: string;
  tone?: 'plain' | 'good' | 'warn' | 'owed';
}): ReactNode {
  const display =
    count !== undefined
      ? new Intl.NumberFormat('en-US').format(count)
      : value === null || value === undefined
        ? '—'
        : moneyCompact(value, currency ?? 'NGN');

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3.5">
      <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</p>
      <p
        className={cn(
          'mt-1 text-lg font-semibold tabular-nums',
          tone === 'good' && 'text-emerald-700',
          tone === 'warn' && 'text-amber-700',
          tone === 'owed' && 'text-rose-700',
          tone === 'plain' && 'text-slate-900',
        )}
        title={value != null && currency ? money(value, currency) : undefined}
      >
        {display}
      </p>
      {hint ? <p className="mt-0.5 text-xs text-slate-400">{hint}</p> : null}
    </div>
  );
}

export function SectionTitle({
  children,
  aside,
}: {
  children: ReactNode;
  aside?: ReactNode;
}): ReactNode {
  return (
    <div className="mb-2.5 flex items-center justify-between gap-3">
      <h2 className="text-sm font-semibold text-slate-900">{children}</h2>
      {aside}
    </div>
  );
}

/**
 * How late a week is, in words.
 *
 * `daysLate` is negative before the due date. "Due in 2 days" and "2 days
 * late" are the two things a collector needs; "−2" is neither.
 */
export function lateness(daysLate: number): string {
  if (daysLate === 0) return 'Due today';
  if (daysLate < 0) {
    const days = -daysLate;
    return `Due in ${days} ${days === 1 ? 'day' : 'days'}`;
  }
  return `${daysLate} ${daysLate === 1 ? 'day' : 'days'} late`;
}

/** A progress bar for an agreement. Deposit counts, because the rider paid it. */
export function Progress({ percent }: { percent: number }): ReactNode {
  const clamped = Math.max(0, Math.min(100, percent));
  return (
    <div
      className="h-1.5 w-full overflow-hidden rounded-full bg-slate-100"
      role="progressbar"
      aria-valuenow={clamped}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label="Paid off"
    >
      <div
        className={cn('h-full rounded-full', clamped >= 100 ? 'bg-emerald-500' : 'bg-brand-500')}
        style={{ width: `${clamped}%` }}
      />
    </div>
  );
}

/**
 * An investor's name, opening their profile in Finance → Investors — the one
 * place their money is read from. Plain text for somebody who may not see
 * investors, so nobody is sent to a screen that turns them away.
 */
export function InvestorName({
  investorId,
  name,
  className,
}: {
  investorId: string | null;
  name: string;
  className?: string;
}): ReactNode {
  const { can } = useAuth();
  if (!investorId || !can('investments:read')) return <span className={className}>{name}</span>;
  return (
    <Link
      to={`/finance/investors/${investorId}`}
      className={cn('text-violet-700 hover:underline', className)}
      aria-label={`Open investor ${name}`}
    >
      {name}
    </Link>
  );
}
